import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
	ChevronDownIcon,
	SparklesIcon,
	SunIcon,
	MoonIcon,
} from '@heroicons/react/24/outline'
import Logo from '../../media/logo.svg'

import {
	CogIcon,
	ArrowRightStartOnRectangleIcon,
	ShieldCheckIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { fetchWithAuth } from '../../utils/apiClient'
import { useMe } from '../../context/MeContext'
import useGlobalTheme, { broadcastThemeChange } from '../../hooks/useGlobalTheme'
import UpgradeModal from '../Billing/UpgradeModal'

const decodeJWT = (token) => {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		return JSON.parse(atob(base64))
	} catch {
		return null
	}
}

const APPS = [
	{
		name: 'LoadLine',
		description: 'DAT Loadboard Tools',
		url: 'https://chromewebstore.google.com/detail/loadline-all-in-one-dispa/cpcnkckpbpefgkcilhclfdefccflgaif',
		icon: Logo,
	},
	{
		name: 'LoadFetcher',
		description: 'Amazon Relay Tools',
		url: 'https://chromewebstore.google.com/detail/loadfetcher-relay-amazon/ihcgicdogclbieclnldfjmlglkolankb',
		icon: '/lf-logo192.png',
	},
]

const truncate = (text, maxLength = 15) => {
	if (text.length <= maxLength + 3) return text

	return text.substr(0, maxLength - 3) + '...'
}

const THEME_KEY = 'loadboard:theme'

function AuthNavBar({ children, compact = false }) {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isAppsOpen, setIsAppsOpen] = useState(false)
	const [isLoggingOut, setIsLoggingOut] = useState(false)
	const [isUpgradeOpen, setIsUpgradeOpen] = useState(false)
	const [checkoutPlanKey, setCheckoutPlanKey] = useState(null)

	const dropdownRef = useRef(null)
	const appsRef = useRef(null)
	const navigate = useNavigate()
	const { me } = useMe()

	const effectiveTheme = useGlobalTheme()
	const toggleTheme = () => {
		const next = effectiveTheme === 'light' ? 'dark' : 'light'
		try {
			localStorage.setItem(THEME_KEY, next)
		} catch {
			// localStorage can fail in private mode — non-fatal
		}
		broadcastThemeChange(next)
	}

	const jwtToken = localStorage.getItem('jwtToken')
	const user = jwtToken ? decodeJWT(jwtToken) : null
	const showUpgrade = me && !me.subscription?.isActive
	const initials = (user?.email || '').slice(0, 2).toUpperCase()

	const isLight = effectiveTheme === 'light'
	const navBg = isLight ? 'bg-white' : 'bg-[#15202b]'
	const avatarCls = isLight
		? 'bg-sky-100 text-sky-700 hover:bg-sky-200'
		: 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
	const appsBtnCls = isLight
		? 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
		: 'text-gray-400 hover:text-white hover:bg-white/10'
	const popoverBg = isLight
		? 'bg-white ring-1 ring-gray-200 shadow-lg'
		: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
	const dropdownItemCls = isLight
		? 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
		: 'text-gray-300 hover:bg-[#1c2834] hover:text-sky-400'
	const dropdownDivider = isLight ? 'border-gray-200' : 'border-gray-600'
	const appsAppName = isLight ? 'text-gray-900' : 'text-gray-200'
	const appsAppDesc = 'text-gray-500'
	const appsHover = isLight ? 'hover:bg-gray-50' : 'hover:bg-white/5'

	const openUpgrade = () => setIsUpgradeOpen(true)
	const closeUpgrade = () => {
		if (checkoutPlanKey) return
		setIsUpgradeOpen(false)
	}

	const handleSelectPlan = async planKey => {
		if (checkoutPlanKey) return
		setCheckoutPlanKey(planKey)
		try {
			const res = await fetchWithAuth('/api/stripe/checkout', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					planKey,
					returnPath: window.location.pathname + window.location.search,
				}),
			})
			const body = await res.json()
			if (!res.ok || !body.url) {
				throw new Error(body.message || 'Could not start checkout.')
			}
			window.location.assign(body.url)
		} catch (err) {
			console.error('[Upgrade] failed:', err)
			setCheckoutPlanKey(null)
		}
	}

	useEffect(() => {
		function handleClickOutside(event) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false)
			}
			if (appsRef.current && !appsRef.current.contains(event.target)) {
				setIsAppsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleLogout = async () => {
		if (isLoggingOut) return
		setIsLoggingOut(true)
		try {
			await fetchWithAuth('/api/user/logout-session', { method: 'POST' })
		} catch (error) {
			console.error('Error logging out session:', error)
		} finally {
			localStorage.removeItem('jwtToken')
			localStorage.removeItem('email')
			navigate('/')
		}
	}

	return (
		<>
		<nav className={`${navBg} ${compact ? 'py-1.5' : 'py-2'}`}>
			<div className={`flex items-center ${compact ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 gap-3' : 'px-4 justify-end'}`}>
				{compact && (
					<div className="flex-1 min-w-0">
						{children}
					</div>
				)}

				<div className="flex items-center gap-3 mr-4 shrink-0">
					{showUpgrade && (
						<button
							onClick={openUpgrade}
							className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-sky-500 to-indigo-500 text-white hover:from-sky-400 hover:to-indigo-400 transition-colors"
							title="Upgrade plan"
						>
							<SparklesIcon className="h-4 w-4" />
							<span>Upgrade</span>
						</button>
					)}
					<div className="relative" ref={appsRef}>
						<button
							onClick={() => {
								setIsAppsOpen(!isAppsOpen)
								setIsDropdownOpen(false)
							}}
							className={`p-2 rounded-full transition duration-200 ${appsBtnCls}`}
							title="Apps"
						>
							<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
								<circle cx="5" cy="5" r="2" />
								<circle cx="12" cy="5" r="2" />
								<circle cx="19" cy="5" r="2" />
								<circle cx="5" cy="12" r="2" />
								<circle cx="12" cy="12" r="2" />
								<circle cx="19" cy="12" r="2" />
								<circle cx="5" cy="19" r="2" />
								<circle cx="12" cy="19" r="2" />
								<circle cx="19" cy="19" r="2" />
							</svg>
						</button>
						{isAppsOpen && (
							<div className={`absolute right-0 mt-2 w-64 rounded-lg py-3 px-3 z-40 ${popoverBg}`}>
								<div className="flex flex-col gap-1">
									{APPS.map(app => (
										<a
											key={app.name}
											href={app.url}
											target="_blank"
											rel="noopener noreferrer"
											className={`flex items-center gap-3 p-3 rounded-lg transition duration-200 ${appsHover}`}
											onClick={() => setIsAppsOpen(false)}
										>
											<img src={app.icon} alt={app.name} className="w-9 h-9 rounded-full" />
											<div>
												<div className={`text-sm font-medium ${appsAppName}`}>
													{app.name}
												</div>
												<div className={`text-xs ${appsAppDesc}`}>{app.description}</div>
											</div>
										</a>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="relative" ref={dropdownRef}>
						<button
							onClick={() => {
								setIsDropdownOpen(!isDropdownOpen)
								setIsAppsOpen(false)
							}}
							className={`flex items-center space-x-2 transition-colors ${
								isLight
									? 'text-sky-900 hover:text-black'
									: 'text-sky-400 hover:text-sky-200'
							}`}
							title={user?.email || ''}
							aria-label="Account menu"
						>
							<span className="font-medium hidden sm:inline">
								{truncate(`${user?.email}`, 22)}
							</span>
							<span className={`sm:hidden flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold uppercase ${avatarCls}`}>
								{user?.email?.charAt(0)}
							</span>
							<ChevronDownIcon className="h-4 w-4 hidden sm:block" />
							</button>
							{isDropdownOpen && (
								<div className={`absolute right-0 mt-2 w-48 rounded-md py-1 z-40 ${popoverBg}`}>
									{user?.isAdmin && (
										<Link
											to="/research"
										className={`flex items-center px-4 py-2 text-sm ${dropdownItemCls}`}
										onClick={() => setIsDropdownOpen(false)}
									>
										<MagnifyingGlassIcon className="h-4 w-4 mr-2" />
										Research
									</Link>
								)}
								<Link
									to="/settings"
									className={`flex items-center px-4 py-2 text-sm ${dropdownItemCls}`}
									onClick={() => setIsDropdownOpen(false)}
								>
									<CogIcon className="h-4 w-4 mr-2" />
									Settings
								</Link>
								{user?.isAdmin && (
									<Link
										to="/admin"
										className={`flex items-center px-4 py-2 text-sm ${dropdownItemCls}`}
										onClick={() => setIsDropdownOpen(false)}
									>
										<ShieldCheckIcon className="h-4 w-4 mr-2" />
										Admin
									</Link>
								)}
								<button
									onClick={() => {
										toggleTheme()
										setIsDropdownOpen(false)
									}}
									className={`flex items-center w-full text-left px-4 py-2 text-sm ${dropdownItemCls}`}
								>
									{effectiveTheme === 'light' ? (
										<MoonIcon className="h-4 w-4 mr-2" />
									) : (
										<SunIcon className="h-4 w-4 mr-2" />
									)}
									{effectiveTheme === 'light' ? 'Dark' : 'Light'}
								</button>
								<hr className={`mx-2 ${dropdownDivider}`} />
								<button
									onClick={handleLogout}
									disabled={isLoggingOut}
									className={`flex items-center w-full text-left px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${dropdownItemCls}`}
								>
									{isLoggingOut ? (
										<svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
									) : (
										<ArrowRightStartOnRectangleIcon className="h-4 w-4 mr-2" />
									)}
									{isLoggingOut ? 'Logging out...' : 'Logout'}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</nav>
		<UpgradeModal
			isOpen={isUpgradeOpen}
			onClose={closeUpgrade}
			onSelectPlan={handleSelectPlan}
			loadingPlanKey={checkoutPlanKey}
		/>
		</>
	)
}

export default AuthNavBar
